import Phaser from 'phaser';
import { JOKER_RANK } from '@dalmuti/shared';
import { THEME } from './theme.js';

export interface CardSpriteOptions {
  rank: number;
  width?: number;
  height?: number;
  selected?: boolean;
  scale?: number;
  faceDown?: boolean;
}

// PNG card ratio is ~414/670 ≈ 0.618. Pick width/height that respect that.
export const CARD_WIDTH = 70;
export const CARD_HEIGHT = 113;

export class CardSprite extends Phaser.GameObjects.Container {
  rank: number;
  private image!: Phaser.GameObjects.Image;
  private overlay!: Phaser.GameObjects.Graphics;
  private rankLabel?: Phaser.GameObjects.Text;
  private currentSelected = false;
  private currentDisabled = false;
  private currentHover = false;
  private cardW: number;
  private cardH: number;

  constructor(scene: Phaser.Scene, options: CardSpriteOptions) {
    super(scene, 0, 0);
    this.rank = options.rank;
    this.cardW = options.width ?? CARD_WIDTH;
    this.cardH = options.height ?? CARD_HEIGHT;
    this.build(options.faceDown === true);
    this.setSelected(options.selected === true);
    this.setSize(this.cardW, this.cardH);
    if (options.scale !== undefined) this.setScale(options.scale);
    scene.add.existing(this);
  }

  private textureKey(faceDown: boolean): string {
    if (faceDown) return 'card-back';
    if (this.rank === JOKER_RANK) return 'card-joker';
    return `card-${this.rank}`;
  }

  private build(faceDown: boolean): void {
    const key = this.textureKey(faceDown);
    this.image = this.scene.add.image(0, 0, key);
    this.image.setDisplaySize(this.cardW, this.cardH);
    this.overlay = this.scene.add.graphics();

    if (!faceDown) {
      const label = this.rank === JOKER_RANK ? 'J' : String(this.rank);
      const fontSize = Math.max(14, Math.round(this.cardW * 0.28));
      this.rankLabel = this.scene.add
        .text(-this.cardW / 2 + 4, -this.cardH / 2 + 2, label, {
          fontFamily: 'Georgia, serif',
          fontSize: `${fontSize}px`,
          color: '#3a2410',
          fontStyle: 'bold',
          stroke: '#fff7e0',
          strokeThickness: 4,
        })
        .setOrigin(0, 0);
      this.add([this.image, this.overlay, this.rankLabel]);
    } else {
      this.add([this.image, this.overlay]);
    }
    this.redraw();
  }

  private redraw(): void {
    const selected = this.currentSelected;
    const hover = this.currentHover;
    const g = this.overlay;
    const w = this.cardW;
    const h = this.cardH;
    g.clear();

    // Outer golden glow when selected
    if (selected) {
      g.fillStyle(0xefc659, 0.55);
      g.fillRoundedRect(-w / 2 - 6, -h / 2 - 6, w + 12, h + 12, 12);
    }

    // Rounded border to frame the card
    const borderColor = selected ? 0xefc659 : hover ? 0x8a5e1f : 0xc89b2f;
    const borderWidth = selected ? 4 : hover ? 3 : 2;
    g.lineStyle(borderWidth, borderColor, 1);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, 7);
  }

  setSelected(value: boolean): void {
    this.currentSelected = value;
    this.redraw();
    this.applyDisabledTint();
  }

  isSelected(): boolean {
    return this.currentSelected;
  }

  setDisabled(value: boolean): void {
    if (this.currentDisabled === value) return;
    this.currentDisabled = value;
    this.applyDisabledTint();
  }

  isDisabled(): boolean {
    return this.currentDisabled;
  }

  setHover(value: boolean): void {
    if (this.currentHover === value) return;
    this.currentHover = value && !this.currentDisabled;
    this.redraw();
  }

  private applyDisabledTint(): void {
    this.setAlpha(this.currentDisabled ? 0.35 : 1);
  }
}

// THEME is retained for downstream importers; mark as intentionally used.
void THEME;
